import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cogoToast from 'cogo-toast';
import slugify from 'slugify';
import _ from 'lodash';
import cn from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faEyeSlash, faChevronUp, faTag } from '@fortawesome/pro-light-svg-icons';
import { faExclamationTriangle, faStar } from '@fortawesome/pro-regular-svg-icons';

import './HierarchyView.scss';

import Tooltip from '../../General/Tooltip';

import { addCategory, addDepartment, addCatalogTag } from '../../../api/catalog';

class HierarchyView extends Component {
  static propTypes = {
    catalog: PropTypes.object.isRequired,
    syncCatalogHierarchy: PropTypes.func.isRequired,
    selectItem: PropTypes.func.isRequired,
    selectedItem: PropTypes.object
  };

  state = {
    activeIndustry: null,
    activeDepartment: null,
    activeCategory: null,
    activeTag: null,
    newIndustryVal: '',
    newDepartmentVal: '',
    newCategoryVal: '',
    newTagVal: ''
  };

  toggleIndustry = industry => {
    const turningOff = industry.id === _.get(this.state, 'activeIndustry.id') && !this.state.activeDepartment;
    this.setState({
      activeIndustry: turningOff ? null : industry,
      activeDepartment: null,
      activeCategory: null,
      activeTag: null
    });
    this.props.selectItem(turningOff ? null : industry);
  };

  toggleDepartment = department => {
    const turningOff = department.id === _.get(this.state, 'activeDepartment.id') && !this.state.activeCategory;
    this.setState({
      activeDepartment: turningOff ? null : department,
      activeCategory: null,
      activeTag: null
    });
    this.props.selectItem(turningOff ? this.state.activeIndustry : department);
  };

  toggleCategory = category => {
    const turningOff = category.id === _.get(this.state, 'activeCategory.id') && !this.state.activeTag;
    this.setState({
      activeCategory: turningOff ? null : category,
      activeTag: null
    });
    this.props.selectItem(turningOff ? null : category);
  };

  toggleTag = tag => {
    const turningOff = tag.id === _.get(this.state, 'activeTag.id');
    this.setState({
      activeTag: turningOff ? null : tag
    });
    this.props.selectItem(turningOff ? this.state.activeCategory : tag);
  };

  addNewDepartment = e => {
    const { newDepartmentVal } = this.state;
    e.preventDefault();
    addDepartment({
      name: newDepartmentVal,
      Industry_id: this.state.activeIndustry.id,
      seoTitle: `Best in ${newDepartmentVal}`,
      urlStub: slugify(`Best in ${newDepartmentVal}`)
    }).then(
      resp => {
        this.props.syncCatalogHierarchy();
      },
      err => {
        console.error(err);
        cogoToast.error(JSON.stringify(err));
      }
    );
    this.setState({
      newDepartmentVal: ''
    });
  };

  addNewCategory = e => {
    const { newCategoryVal, activeDepartment } = this.state;
    e.preventDefault();
    addCategory({
      name: newCategoryVal,
      Department_id: activeDepartment.id,
      seoTitle: `Best in ${newCategoryVal}`,
      urlStub: slugify(`Best in ${newCategoryVal}`)
    }).then(
      resp => {
        this.props.syncCatalogHierarchy();
      },
      err => {
        console.error(err);
        cogoToast.error(JSON.stringify(err));
      }
    );
    this.setState({
      newCategoryVal: ''
    });
  };

  addNewTag = e => {
    const { newTagVal, activeIndustry, activeDepartment, activeCategory } = this.state;
    e.preventDefault();
    addCatalogTag({
      name: newTagVal,
      Industry_id: activeIndustry.id,
      Department_id: activeDepartment.id,
      Category_id: activeCategory.id,
      seoTitle: `Best in ${activeCategory.name} | ${newTagVal}`,
      urlStub: slugify(`Best in ${activeCategory.name} ${newTagVal}`).toLowerCase()
    }).then(
      resp => this.props.syncCatalogHierarchy(),
      err => {
        console.error(err);
        cogoToast.error(JSON.stringify(err));
      }
    );
    this.setState({ newTagVal: '' });
  };

  isSEOComplete = item => {
    return _.get(item, 'seoDescription.length') > 0 && _.get(item, 'urlStub.length') > 0;
  };

  getBadgeIcons = (item, active) => {
    const badges = [
      {
        icon: faTag,
        condition: item => item.isAutoTag,
        tooltip: 'Auto Tagged'
      },
      {
        icon: faEyeSlash,
        condition: item => item.isHidden,
        tooltip: 'Hidden'
      },
      {
        icon: faStar,
        condition: item => !_.isEmpty(item.feature),
        tooltip: 'Featured'
      },
      {
        icon: faExclamationTriangle,
        condition: item => !this.isSEOComplete(item),
        tooltip: 'SEO Incomplete'
      },
      {
        icon: faChevronDown,
        condition: item => !!item.children,
        tooltip: 'Has Children'
      },
      {
        icon: active ? faChevronUp : faChevronDown,
        condition: () => true,
        tooltip: ''
      }
    ];
    return badges.map((badge, idx) => {
      return (
        !!badge.condition(item) && (
          <Tooltip key={`${item.id}-${idx}`} message={badge.tooltip} getIconDiv={() => <FontAwesomeIcon icon={badge.icon}></FontAwesomeIcon>} />
        )
      );
    });
  };

  getAddNewForm = options => {
    const { addFn, placeholder, value, onUpdateValue } = options;
    return (
      <form onSubmit={addFn} className='add-element-form level-three'>
        <input placeholder={placeholder} value={value} onChange={({ target }) => onUpdateValue(target.value)} />
      </form>
    );
  };

  render() {
    const { catalog } = this.props;
    const { activeIndustry, activeDepartment, activeCategory, activeTag, newDepartmentVal, newCategoryVal, newTagVal } = this.state;
    const { industries } = catalog;

    if (!industries?.length) return <div>Loading Industries...</div>;

    const sortedIndustries = _.orderBy(industries, 'name', 'asc');

    return (
      <div className='hierarchy-view-outer-container panel'>
        {sortedIndustries.map(industry => {
          const { departments } = industry;
          const active = activeIndustry?.id === industry.id;
          const select = () => this.toggleIndustry(industry);
          const orderedDepartments = _.orderBy(departments, 'name', 'asc');
          return (
            <React.Fragment key={industry.id}>
              <div onClick={select} className={cn('row', { active, empty: !departments.length })}>
                <div className='main'>
                  {industry.name} ({departments.length})
                </div>
                <div className='secondary'>{this.getBadgeIcons(industry, active)}</div>
              </div>
              {active &&
                orderedDepartments.map(department => {
                  const { categories } = department;
                  const active = activeDepartment?.id === department.id;
                  const select = () => this.toggleDepartment(department);
                  const orderedCategories = _.orderBy(categories, 'name', 'asc');
                  return (
                    <React.Fragment key={department.id}>
                      <div onClick={select} className={cn('row level-two', { active, empty: !categories.length })}>
                        <div className='main'>
                          {department.name} ({categories.length})
                        </div>
                        <div className='secondary'>{this.getBadgeIcons(department, active)}</div>
                      </div>
                      {active &&
                        orderedCategories.map(category => {
                          const { tags } = category;
                          const active = activeCategory?.id === category.id;
                          const select = () => this.toggleCategory(category);
                          const orderedTags = _.orderBy(tags, 'name', 'asc');
                          return (
                            <React.Fragment key={category.id}>
                              <div onClick={select} className={cn('row level-three', { active })}>
                                <div className='main'>{category.name}</div>
                                <div className='secondary'>{this.getBadgeIcons(category, active)}</div>
                              </div>
                              {active &&
                                orderedTags.map(tag => {
                                  const active = activeTag?.id === tag.id;
                                  const select = () => this.toggleTag(tag);
                                  return (
                                    <div onClick={select} key={tag.id} className={cn('row level-four', { active })}>
                                      {tag.name}
                                    </div>
                                  );
                                })}
                              {active &&
                                !activeTag &&
                                this.getAddNewForm({
                                  addFn: this.addNewTag,
                                  placeholder: `Add New ${category.name} Tag`,
                                  value: newTagVal,
                                  onUpdateValue: value => this.setState({ newTagVal: value })
                                })}
                            </React.Fragment>
                          );
                        })}

                      {active &&
                        !activeCategory &&
                        this.getAddNewForm({
                          addFn: this.addNewCategory,
                          placeholder: `Add New ${activeDepartment.name} Category`,
                          value: newCategoryVal,
                          onUpdateValue: value => this.setState({ newCategoryVal: value })
                        })}
                    </React.Fragment>
                  );
                })}

              {active &&
                !activeDepartment &&
                this.getAddNewForm({
                  addFn: this.addNewDepartment,
                  placeholder: `Add New ${activeIndustry.name} Department`,
                  value: newDepartmentVal,
                  onUpdateValue: value => this.setState({ newDepartmentVal: value })
                })}
            </React.Fragment>
          );
        })}
      </div>
    );
  }
}

export default HierarchyView;
